import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { adminGetOfferPopup, adminSaveOfferPopup, adminUploadOfferImage } from "../../services/api";
import { getProductImage } from "../../data/images";

const label = "text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal";
const input = "mt-2 w-full border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-orange";

const EMPTY = {
  enabled: false,
  title: "",
  subtitle: "",
  discount_text: "",
  cta_text: "",
  cta_link: "",
  banner_image: "",
  start_at: "",
  end_at: "",
};

export default function AdminOffer() {
  const { user, ready } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && user?.role === "admin") {
      adminGetOfferPopup()
        .then((d) => setForm({ ...EMPTY, ...d }))
        .catch(() => toast.error("Could not load offer popup."))
        .finally(() => setLoading(false));
    }
  }, [ready, user]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const upload = async (file) => {
    if (!file) return;
    try {
      const res = await adminUploadOfferImage(file);
      setForm((f) => ({ ...f, banner_image: res.path }));
      toast.success("Banner uploaded.");
    } catch {
      toast.error("Banner upload failed.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveOfferPopup(form);
      toast.success("Offer popup saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const bannerSrc = form.banner_image ? getProductImage(form.banner_image, "offer banner").src : null;

  return (
    <AdminLayout
      title="Offer Popup"
      subtitle="Festival & promotion popup — controlled from here, no code changes needed"
      actions={
        <button
          onClick={save}
          disabled={saving || loading}
          data-testid="offer-save-btn"
          className="bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-orange disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      }
    >
      {loading ? (
        <p className="text-brand-stone" data-testid="offer-loading">Loading…</p>
      ) : (
        <div className="max-w-2xl space-y-6" data-testid="admin-offer-page">
          <label className="flex cursor-pointer items-center gap-3 border border-brand-sand bg-brand-surface p-4 text-sm text-brand-charcoal">
            <input type="checkbox" data-testid="offer-enabled" className="h-5 w-5 accent-brand-orange" checked={form.enabled} onChange={set("enabled")} />
            <span className="font-medium">Enable popup</span>
            <span className="text-xs text-brand-stone">Shows only when enabled and within the date/time window below.</span>
          </label>

          <div>
            <label className={label}>Offer title</label>
            <input data-testid="offer-title" className={input} value={form.title} onChange={set("title")} placeholder="e.g. Diwali Festive Edit" />
          </div>
          <div>
            <label className={label}>Message / subtitle</label>
            <textarea data-testid="offer-subtitle" className={`${input} min-h-[80px]`} value={form.subtitle} onChange={set("subtitle")} placeholder="A short line about the offer" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label className={label}>Discount text / code</label>
              <input data-testid="offer-discount" className={input} value={form.discount_text} onChange={set("discount_text")} placeholder="e.g. FESTIVE33" />
            </div>
            <div>
              <label className={label}>CTA button text</label>
              <input data-testid="offer-cta-text" className={input} value={form.cta_text} onChange={set("cta_text")} placeholder="e.g. Shop the Edit" />
            </div>
          </div>
          <div>
            <label className={label}>CTA link</label>
            <input data-testid="offer-cta-link" className={input} value={form.cta_link} onChange={set("cta_link")} placeholder="/shop or https://…" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label className={label}>Start date/time</label>
              <input type="datetime-local" data-testid="offer-start" className={input} value={form.start_at || ""} onChange={set("start_at")} />
            </div>
            <div>
              <label className={label}>End date/time</label>
              <input type="datetime-local" data-testid="offer-end" className={input} value={form.end_at || ""} onChange={set("end_at")} />
            </div>
          </div>

          <div>
            <label className={label}>Banner image (optional)</label>
            <div className="mt-2 flex items-center gap-3">
              <div className="h-16 w-28 overflow-hidden bg-white">
                {bannerSrc ? <img src={bannerSrc} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-brand-stone">None</div>}
              </div>
              <label data-testid="offer-image-upload" className="cursor-pointer border border-brand-sand px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal hover:border-brand-orange">
                Upload<input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => upload(e.target.files[0])} />
              </label>
              {form.banner_image && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, banner_image: "" }))} data-testid="offer-image-remove" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-stone hover:text-red-600">
                  Remove
                </button>
              )}
            </div>
          </div>

          <p className="text-xs text-brand-stone">Tip: The popup appears about 2–3 seconds after a visitor opens the site and is remembered after they close it.</p>
        </div>
      )}
    </AdminLayout>
  );
}
